// frontend/src/components/CustomerForm.jsx
import React, { useState } from "react";

export default function CustomerForm({ onCustomerAdded, fullBleed = true }) {
  const [formData, setFormData] = useState({
    first_name: "", surname: "",
    address: "", address2: "", address3: "",
    town: "", province: "", postcode: "",
    tel: "", email: "", nie: "",
  });

  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [okMsg, setOkMsg] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  async function handleSubmit(e) {
    e.preventDefault();
    setErr(""); setOkMsg(""); setSubmitting(true);
    try {
      const res = await fetch("/api/customers", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      if (!res.ok) throw new Error((await res.text().catch(() => "")) || `HTTP ${res.status}`);
      const newCustomer = await res.json();

      setOkMsg("Customer added successfully.");
      setFormData({
        first_name: "", surname: "",
        address: "", address2: "", address3: "",
        town: "", province: "", postcode: "",
        tel: "", email: "", nie: "",
      });
      onCustomerAdded?.(newCustomer);
    } catch (e2) {
      setErr(String(e2.message || e2));
    } finally {
      setSubmitting(false);
    }
  }

  function handleReset() {
    setFormData({
      first_name: "", surname: "",
      address: "", address2: "", address3: "",
      town: "", province: "", postcode: "",
      tel: "", email: "", nie: "",
    });
    setErr(""); setOkMsg("");
  }

  return (
    <div className={fullBleed ? "cf-fullbleed" : undefined}>
      <div className="card">
        <style>{`
          /* --- Full-bleed wrapper --- */
          .cf-fullbleed {
            min-height: 1px;
            padding: 16px clamp(12px, 2vw, 24px);
            position: relative;
            left: 50%;
            right: 50%;
            margin-left: calc(50% - 50vw);
            margin-right: calc(50% - 50vw);
            width: 100vw;
            max-width: 100vw;
            background: var(--card, #0a2a57);
          }

          /* Kill the full-bleed math on small screens to avoid a ~50% left gap */
          @media (max-width: 640px) {
            .cf-fullbleed {
              left: auto;
              right: auto;
              margin-left: 0;
              margin-right: 0;
              width: 100%;
              max-width: 100%;
            }
          }

          /* --- Card sections --- */
          .cf-header {
            padding: 10px 12px;
            border-bottom: 1px solid var(--border);
            font-weight: 700;
          }
          .cf-body { padding: 12px; }

          /* --- Grid layout --- */
          .cf-grid {
            display: grid;
            gap: 12px;
            grid-template-columns: 1fr;
          }
          @media (min-width: 520px) {
            .cf-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          }
          @media (min-width: 980px) {
            .cf-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
          }

          .cf-field label {
            display: block;
            margin-bottom: 6px;
            font-size: 12px;
            color: #6b7280; /* muted */
            font-weight: 600;
          }

          .cf-actions {
            display: flex;
            gap: 8px;
            justify-content: flex-end;
            margin-top: 10px;
            flex-wrap: wrap;
          }
          .muted { opacity: .8; }
        `}</style>

        <div className="cf-header">Add Customer</div>

        <div className="cf-body" style={{ paddingBottom: 0 }}>
          {err && (
            <div style={{
              marginBottom: 12, padding: "10px 12px", borderRadius: 8,
              border: "1px solid #fecaca", background: "#fee2e2", color: "#991b1b",
            }}>
              {err}
            </div>
          )}
          {okMsg && (
            <div style={{
              marginBottom: 12, padding: "10px 12px", borderRadius: 8,
              border: "1px solid #bbf7d0", background: "#dcfce7", color: "#166534",
            }}>
              {okMsg}
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit}>
          <div className="cf-body">
            <div className="cf-grid">
              <div className="cf-field">
                <label>First Name</label>
                <input className="input" name="first_name" value={formData.first_name} onChange={handleChange} required />
              </div>
              <div className="cf-field">
                <label>Surname</label>
                <input className="input" name="surname" value={formData.surname} onChange={handleChange} required />
              </div>

              <div className="cf-field" style={{ gridColumn: "1 / -1" }}>
                <label>Address Line 1</label>
                <input className="input" name="address" value={formData.address} onChange={handleChange} required />
              </div>
              <div className="cf-field">
                <label>Address Line 2</label>
                <input className="input" name="address2" value={formData.address2} onChange={handleChange} />
              </div>
              <div className="cf-field">
                <label>Address Line 3</label>
                <input className="input" name="address3" value={formData.address3} onChange={handleChange} />
              </div>

              <div className="cf-field">
                <label>Town</label>
                <input className="input" name="town" value={formData.town} onChange={handleChange} />
              </div>
              <div className="cf-field">
                <label>Province</label>
                <input className="input" name="province" value={formData.province} onChange={handleChange} />
              </div>
              <div className="cf-field">
                <label>Postcode</label>
                <input className="input" name="postcode" value={formData.postcode} onChange={handleChange} required />
              </div>

              <div className="cf-field">
                <label>Telephone</label>
                <input className="input" name="tel" value={formData.tel} onChange={handleChange} required />
              </div>
              <div className="cf-field">
                <label>Email</label>
                <input className="input" type="email" name="email" value={formData.email} onChange={handleChange} required />
              </div>
              <div className="cf-field">
                <label>NIE Number</label>
                <input className="input" name="nie" value={formData.nie} onChange={handleChange} />
              </div>
            </div>

            <div className="cf-actions">
              <button type="button" className="btn secondary" onClick={handleReset} disabled={submitting}>Reset</button>
              <button type="submit" className="btn" disabled={submitting}>
                {submitting ? "Adding…" : "Add Customer"}
              </button>
            </div>

            <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
              Fields marked as required must be completed before saving.
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
